import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getStartGuideContent } from "./content";
import { useProjectStartGuide } from "./status/useProjectStartGuide";
import "./StartGuide.sass";

const SUPPORTED = ["LEPR", "LAuthorize", "LTax", "LTax_agent"];
const ACTIVE_KEY = "lappa.startGuide.active";

function isStartGuideRoute(pathname = "") {
  return pathname === "/start-guide" || pathname.startsWith("/start-guide/");
}

function readStartGuideActive() {
  try {
    return window.sessionStorage.getItem(ACTIVE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeStartGuideActive(active) {
  try {
    if (active) window.sessionStorage.setItem(ACTIVE_KEY, "1");
    else window.sessionStorage.removeItem(ACTIVE_KEY);
  } catch {
    // Private mode can block session storage. The banner then stays hidden.
  }
}

function locationMatchesGuideStep(project, location) {
  const steps = getStartGuideContent(project)?.steps || {};
  return Object.values(steps).some((step) => {
    const route = routeOf(step?.to);
    if (!route || route === "/") return false;
    return location.pathname === route || location.pathname.startsWith(`${route}/`);
  });
}

function routeOf(to = "") {
  return String(to).split("?")[0];
}

function Destination({ project }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const content = getStartGuideContent(project);
  const guide = useProjectStartGuide(project, content);
  const required = guide.steps.filter((step) => step.category === "required");
  const matches = guide.steps.filter((step) => {
    const route = routeOf(content.steps?.[step.id]?.to);
    if (!route || step.category === "learn") return false;
    return location.pathname === route || location.pathname.startsWith(`${route}/`);
  });
  const longest = matches.reduce(
    (length, step) => Math.max(length, routeOf(content.steps?.[step.id]?.to).length),
    0
  );
  const closest = matches.filter(
    (step) => routeOf(content.steps?.[step.id]?.to).length === longest
  );
  const match =
    closest.find((step) => !step.complete) || closest[0];

  if (guide.loading || guide.projectGuideVisible === false || !match) return null;

  if (guide.notice?.stepId === match.id) {
    return (
      <div className="StartGuideDestination is-saved">
        <b>{guide.notice.message}</b>
        <button type="button" onClick={() => navigate("/start-guide")}>
          {t("corePages.startGuide.backToGuide")}
        </button>
        <button
          type="button"
          className="banner-custom-close-button"
          aria-label={t("corePages.startGuide.close")}
          onClick={guide.clearNotice}
        >
          ×
        </button>
      </div>
    );
  }

  if (match.complete || match.category === "learn") return null;

  const position = required.findIndex((step) => step.id === match.id);
  const label =
    position >= 0
      ? t("corePages.startGuide.taskOf", { current: position + 1, total: required.length })
      : t("corePages.startGuide.setupTask");

  return (
    <div className="StartGuideDestination">
      <span>{label}</span>
      <b>{match.name}</b>
      <p>{t("corePages.startGuide.completeAfterSave")}</p>
      <button type="button" onClick={() => navigate("/start-guide")}>
        {t("corePages.startGuide.backToGuide")}
      </button>
    </div>
  );
}

export default function StartGuideDestination() {
  const project = useSelector((state) => state.user.openProjectName);
  const showUserGuide = useSelector((state) => Boolean(state.user.userGuide?.show_user_guide));
  const location = useLocation();
  const supported = SUPPORTED.includes(project);
  const onGuide = isStartGuideRoute(location.pathname);
  const onStep = supported && locationMatchesGuideStep(project, location);

  useEffect(() => {
    if (!supported || !showUserGuide) {
      writeStartGuideActive(false);
      return;
    }
    if (onGuide) writeStartGuideActive(true);
    else if (!onStep) writeStartGuideActive(false);
  }, [onGuide, onStep, showUserGuide, supported]);

  if (!showUserGuide || !supported || onGuide || !onStep || !readStartGuideActive()) return null;
  return <Destination project={project} />;
}
